import {
  BudgetConversionInconsistency,
  BudgetConversionInconsistencyApiItem,
  BudgetConversionInconsistencyListResponse,
} from './types';

const DEFAULT_INCONSISTENCY_DESCRIPTION = 'Sem descrição';
const DEFAULT_INFORMED_PRICE_BASE_DESCRIPTION = 'Preço informado';

const toNumberOrNull = (value: unknown): number | null => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }

  return value;
};

const toStringOrNull = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();
  return normalized || null;
};

const toStringOrDash = (value: unknown): string => {
  if (value == null) {
    return '-';
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (typeof value !== 'string') {
    return '-';
  }

  const normalized = value.trim();
  return normalized || '-';
};

const getInputDescription = (
  item: BudgetConversionInconsistencyApiItem,
): string | null => {
  return (
    toStringOrNull(item.input_price?.custom_description) ||
    toStringOrNull(item.input_price?.input?.description)
  );
};

const getCompositionDescription = (
  item: BudgetConversionInconsistencyApiItem,
): string | null => {
  return (
    toStringOrNull(item.budget_composition_price?.description) ||
    toStringOrNull(item.composition_price?.custom_description) ||
    toStringOrNull(item.composition_price?.composition?.description)
  );
};

const getInputCode = (
  item: BudgetConversionInconsistencyApiItem,
): string | null => {
  return toStringOrNull(item.input_price?.input?.code);
};

const getCompositionCode = (
  item: BudgetConversionInconsistencyApiItem,
): string | null => {
  return (
    toStringOrNull(item.budget_composition_price?.code) ||
    toStringOrNull(item.composition_price?.composition?.code)
  );
};

const getBaseDescription = (
  item: BudgetConversionInconsistencyApiItem,
): string => {
  const baseDescription =
    toStringOrNull(item.input_price?.base?.description) ||
    toStringOrNull(item.composition_price?.base?.description) ||
    toStringOrNull(item.budget_composition_price?.base?.description) ||
    '-';

  if (item.type === 'composition_custom' && baseDescription === '-') {
    return DEFAULT_INFORMED_PRICE_BASE_DESCRIPTION;
  }

  return baseDescription;
};

const getReferenceDescription = (
  item: BudgetConversionInconsistencyApiItem,
): string => {
  return (
    toStringOrNull(item.input_price?.version?.description) ||
    toStringOrNull(item.input_price?.input?.version?.description) ||
    toStringOrNull(item.composition_price?.version?.description) ||
    toStringOrNull(item.budget_composition_price?.version?.description) ||
    '-'
  );
};

export const getInconsistencyTypeLabel = (type: string): string => {
  if (type === 'input_original') {
    return 'Insumo avulso na EAP';
  }

  if (type === 'composition_custom') {
    return 'Serviço informado';
  }

  return 'Estrutura Inválida';
};

export const mapConversionInconsistency = (
  item: BudgetConversionInconsistencyApiItem,
): BudgetConversionInconsistency => {
  const description =
    getInputDescription(item) ||
    getCompositionDescription(item) ||
    toStringOrNull(item.title) ||
    DEFAULT_INCONSISTENCY_DESCRIPTION;

  const code =
    (item.type === 'input_original' ? getInputCode(item) : null) ||
    (item.type === 'composition_custom' ? getCompositionCode(item) : null) ||
    getInputCode(item) ||
    getCompositionCode(item);

  return {
    id: item.id,
    type: item.type,
    typeLabel: getInconsistencyTypeLabel(item.type),
    location: toStringOrDash(item.location),
    level: toNumberOrNull(item.level),
    base: getBaseDescription(item),
    reference: getReferenceDescription(item),
    code,
    description,
    quantity: toNumberOrNull(item.quantity),
    unitPrice: toNumberOrNull(item.social_law_price ?? item.unit_price),
    unitPriceWithBDI: toNumberOrNull(item.price ?? item.unit_price),
    total: toNumberOrNull(item.total),
  };
};

export const normalizeInconsistencyResponse = (
  response: BudgetConversionInconsistencyListResponse,
): BudgetConversionInconsistencyListResponse => {
  const responseData = Array.isArray(response.data) ? response.data : [];

  return {
    ...response,
    data: responseData,
  };
};

type DuplicateBudgetResponse = {
  id?: number;
  data?: {
    id?: number;
    data?: {
      id?: number;
    };
  };
};

export const extractDuplicatedBudgetId = (
  response: DuplicateBudgetResponse,
): number | null => {
  if (response.id && response.id > 0) {
    return response.id;
  }

  if (response.data?.id && response.data.id > 0) {
    return response.data.id;
  }

  if (response.data?.data?.id && response.data.data.id > 0) {
    return response.data.data.id;
  }

  return null;
};
